import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Type-only import — erased at build, so it creates NO runtime chunk edge.
// The content index itself is loaded dynamically when the palette first opens
// (see the effect below); this keeps the entire content catalog (activities,
// languages, courses, recipes…) off the initial render path.
import type { ContentRef } from '../../engine/content-registry';

const KIND_LABEL: Record<ContentRef['kind'], string> = {
  game: 'Game',
  'course-lesson': 'Lesson',
  activity: 'Activity',
  language: 'Language',
  mission: 'Mission',
  recipe: 'Recipe',
  'folk-tale': 'Story',
  journey: 'Journey',
  expedition: 'Expedition',
  instrument: 'Instrument',
};

const MAX_RESULTS = 25;

/**
 * Substring-rank scoring — small, dependency-free, good enough for ~900 items.
 * +50 for exact title match, +20 if title starts with query, +10 if title
 * contains query, +5 if description contains query.
 */
function score(item: ContentRef, q: string): number {
  if (!q) return 0;
  const lcQ = q.toLowerCase();
  const lcTitle = item.title.toLowerCase();
  let s = 0;
  if (lcTitle === lcQ) s += 50;
  else if (lcTitle.startsWith(lcQ)) s += 20;
  else if (lcTitle.includes(lcQ)) s += 10;
  if (item.description?.toLowerCase().includes(lcQ)) s += 5;
  // Subject-name match boosts so queries like "math" surface math content.
  if (item.subjects.some(sub => sub.includes(lcQ))) s += 4;
  return s;
}

/**
 * Command palette — global ⌘K / Ctrl+K search across every game, lesson,
 * activity, language, mission, story, recipe, journey, expedition, and
 * instrument on the platform. Backed by the shared content registry.
 *
 * Audit context: with 800+ surfaces, search becomes the fastest path for
 * power users (especially parents adding content to a kid's plan). Shipped
 * as keyboard-shortcut-only first — no extra TopBar UI to clutter the
 * kid-default chrome.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [allContent, setAllContent] = useState<ContentRef[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Lazy-load the content index the first time the palette opens. The registry
  // statically pulls in the whole catalog (every activity/language JSON, every
  // course, recipe, mission…); importing it dynamically here keeps all of that
  // out of the entry chunk so first paint stays lean.
  useEffect(() => {
    if (!open || allContent !== null) return;
    let cancelled = false;
    void import('../../engine/content-registry').then((m) => {
      if (!cancelled) setAllContent(m.getAllContent());
    });
    return () => { cancelled = true; };
  }, [open, allContent]);

  // Global ⌘K / Ctrl+K toggle. `Escape` closes when open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else { setQuery(''); setActiveIdx(0); }
  }, [open]);

  const results = useMemo<ContentRef[]>(() => {
    if (!query.trim() || !allContent) return [];
    const scored: { item: ContentRef; s: number }[] = [];
    for (const item of allContent) {
      const s = score(item, query.trim());
      if (s > 0) scored.push({ item, s });
    }
    scored.sort((a, b) => b.s - a.s);
    return scored.slice(0, MAX_RESULTS).map(x => x.item);
  }, [query, allContent]);

  // Reset highlight when results change.
  useEffect(() => { setActiveIdx(0); }, [results]);

  const choose = (item: ContentRef) => {
    setOpen(false);
    navigate(item.route);
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && results[activeIdx]) {
      e.preventDefault();
      choose(results[activeIdx]);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Search the platform"
        >
          <motion.div
            initial={{ y: -12, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: -12, scale: 0.98 }}
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-black/5 dark:border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-black/5 dark:border-white/10">
              <Search className="w-5 h-5 shrink-0" style={{ color: 'var(--color-theme-text-secondary)' }} aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search games, lessons, stories, recipes…"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
                style={{ color: 'var(--color-theme-text-primary)' }}
                aria-label="Search query"
              />
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close search"
              >
                <X className="w-4 h-4" style={{ color: 'var(--color-theme-text-secondary)' }} />
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto">
              {!query.trim() ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-theme-text-secondary)' }}>
                  Type to search across every game, lesson, story, and activity.
                </div>
              ) : !allContent ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-theme-text-secondary)' }}>
                  Loading search…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-theme-text-secondary)' }}>
                  No results for &ldquo;{query}&rdquo;.
                </div>
              ) : (
                <ul role="listbox">
                  {results.map((r, i) => {
                    const active = i === activeIdx;
                    return (
                      <li key={`${r.kind}-${r.id}`} role="option" aria-selected={active}>
                        <button
                          onMouseEnter={() => setActiveIdx(i)}
                          onClick={() => choose(r)}
                          className={`w-full text-left px-4 py-2.5 flex items-center gap-3 border-b border-black/5 dark:border-white/5 transition
                            ${active ? 'bg-indigo-50 dark:bg-indigo-950/40' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                        >
                          {r.emoji && <span className="text-xl shrink-0" aria-hidden="true">{r.emoji}</span>}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate" style={{ color: 'var(--color-theme-text-primary)' }}>
                              {r.title}
                            </div>
                            {r.description && (
                              <div className="text-xs truncate" style={{ color: 'var(--color-theme-text-secondary)' }}>
                                {r.description}
                              </div>
                            )}
                          </div>
                          <span className="text-[10px] uppercase font-bold tracking-wide shrink-0 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800"
                            style={{ color: 'var(--color-theme-text-secondary)' }}>
                            {KIND_LABEL[r.kind]}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="px-4 py-2 border-t border-black/5 dark:border-white/10 flex items-center justify-between text-[11px]"
              style={{ color: 'var(--color-theme-text-secondary)' }}>
              <span>↑ ↓ to navigate · ↵ to open · esc to close</span>
              <span><kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800">⌘K</kbd></span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
